import { useEffect, useRef, useState } from "react";
import { Link, useMatch, useSearchParams } from "react-router-dom";
import { api, type TermState } from "../api/client";
import { track } from "../lib/events";
import { pushState } from "../lib/push";
import { pushAskDismissed } from "./PushAsk";
import { LEVELS, when } from "../lib/format";
import { useLoad } from "../lib/load";
import { useSession } from "../lib/session";
import { Dots } from "./Dots";

/** The explanation sheet, driven by ?term=ID so the back button closes it and links can open it. */
export function TermSheet() {
  const [params, setParams] = useSearchParams();
  const termId = params.get("term");
  const storyId = useMatch("/stories/:id")?.params.id;
  const { me, requireAuth, bump, toast, openPushAsk } = useSession();
  const ref = useRef<HTMLDivElement>(null);
  // Changes show immediately; the server confirms (or we roll back and say why).
  const [local, setLocal] = useState<{ id: string; patch: Partial<TermState> }>();

  const close = () =>
    setParams((p) => {
      p.delete("term");
      return p;
    });
  const open = (id: string) =>
    setParams((p) => {
      p.set("term", id);
      return p;
    });

  const { data: term, error, reload } = useLoad(
    () => (termId ? api.term(termId, storyId) : Promise.resolve(undefined)),
    [termId, storyId, me?.id],
  );

  useEffect(() => {
    if (!termId) return;
    track("term_clicked", { term_id: termId });
    ref.current?.focus();
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && close();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [termId]);

  if (!termId) return null;

  const state: TermState | undefined = term && { ...term.state, ...(local?.id === term.id ? local.patch : {}) };

  const act = async (patch: Partial<TermState>, fn: () => Promise<unknown>, done?: string) => {
    if (!term || !requireAuth()) return;
    setLocal({ id: term.id, patch: { ...(local?.id === term.id ? local.patch : {}), ...patch } });
    try {
      await fn();
      reload();
      bump();
      if (done) toast(done);
      if (patch.following && !pushAskDismissed() && (await pushState(true)) === "off") openPushAsk();
    } catch (e) {
      setLocal(undefined);
      toast((e as Error).message);
    }
  };

  return (
    <>
      <div className="scrim" onClick={close} />
      <div className="sheet" role="dialog" aria-modal="true" aria-label={term?.name ?? "Term"} tabIndex={-1} ref={ref}>
        <div className="grab" />
        {error && <p className="empty">That term could not be loaded.</p>}
        {term && state && (
          <>
            <div className="row between">
              <span className="mono">TERM</span>
              <span className="row">
                <Dots level={term.level} />
                <span className="mono">{LEVELS[term.level - 1]}</span>
              </span>
            </div>
            <h2>{term.name}</h2>
            {term.full && <p className="full">{term.full}</p>}
            <p className="plain">{term.plain}</p>
            {term.why && (
              <div className="why">
                <b>In this story</b>
                {term.why}
              </div>
            )}
            {term.related.length > 0 && (
              <div className="row">
                <span className="mono">RELATED</span>
                {term.related.map((r) => (
                  <button key={r.id} type="button" className="chip" onClick={() => open(r.id)}>
                    {r.name}
                  </button>
                ))}
              </div>
            )}
            {term.stories.length > 0 && (
              <div className="met">
                <span className="mono">SEEN IN</span>
                {term.stories.map((s) => (
                  <Link key={s.id} to={`/stories/${s.id}`} onClick={close}>
                    {s.title}
                    <small>{when(s.published_at)}</small>
                  </Link>
                ))}
              </div>
            )}
            <div className="acts">
              <label className="check wide">
                <input
                  type="checkbox"
                  checked={state.understood}
                  onChange={(e) =>
                    act({ understood: e.target.checked }, () => api.setTerm(term.id, { understood: e.target.checked }))
                  }
                />
                <span>
                  I understand this term
                  <small>It will stop being highlighted</small>
                </span>
              </label>
              <button
                type="button"
                className="btn ghost"
                aria-pressed={state.saved}
                onClick={() =>
                  act(
                    { saved: !state.saved },
                    () => api.setTerm(term.id, { saved: !state.saved }),
                    state.saved ? "Removed from My terms." : "Saved to My terms.",
                  )
                }
              >
                {state.saved ? "Saved" : "Save to my terms"}
              </button>
              <button
                type="button"
                className="btn ghost"
                aria-pressed={state.following}
                onClick={() =>
                  act(
                    { following: !state.following },
                    () => (state.following ? api.unfollow(term.id) : api.follow(term.id)),
                    state.following ? "Unfollowed." : "Following. We will tell you when it appears.",
                  )
                }
              >
                {state.following ? "Following" : "Follow this term"}
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
}
